import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Subject } from "rxjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import { EMPTY } from "rxjs";
import { BuyerService } from "../../../../core/services/buyer.service";
import {
  FormArray,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { GetAllBuyersRequestDto } from "../../../../core/models/orders/getAllBuyersRequestDto";
import { GetAllBuyersResponseDto } from "../../../../core/models/orders/getAllBuyersResponseDto";
import { ScrollerModule } from "primeng/scroller";
import { TableModule } from "primeng/table";
import { CommonModule } from "@angular/common";
import { Popover, PopoverModule } from "primeng/popover";
import { ClientSearchList } from "../../../../core/models/shared/clientSearchList";
import { GenericResponseDto } from "../../../../core/models/generic-response.dto";
import { InputTextModule } from "primeng/inputtext";
import { DropdownModule } from "primeng/dropdown";

@UntilDestroy()
@Component({
  selector: "jni-buyer",
  templateUrl: "./buyer.component.html",
  styleUrls: ["./buyer.component.scss"],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ScrollerModule,
    TableModule,
    CommonModule,
    PopoverModule,
    InputTextModule,
    DropdownModule,
  ],
})
export class BuyerComponent implements OnInit {
  public buyerVendor$ = new Subject<string>();
  // public customerVendor$ = new Subject<string>();
  buyerLists: GetAllBuyersResponseDto[] = [];
  showSmallLoader = false;
  customerLists: ClientSearchList[] = [];
  customerBillToObject!: ClientSearchList;
  buyerName = "";
  selectedAccountNumber = "";
  @Input() parent!: FormGroup;
  @Input() deliveryAddress: {
    id: number;
    name: string;
    status: boolean;
  }[] = [];
  @Output() smallLoaderEvent = new EventEmitter<boolean>();
  @Input() formErrors!: Record<string, string>;
  @Output() validationErrorEvent = new EventEmitter();
  @Output() clearShipToEvent = new EventEmitter();
  @Output() getBuyerEvent = new EventEmitter();
  @Output() customerBillToObjectEvent = new EventEmitter();

  constructor(private _buyerService: BuyerService) {}

  ngOnInit(): void {
    this.searchBuyerName();
    this.getAllBuyers();
  }

  onBuyerSearchOpenToggle(mydrop: Popover, event: Event, target: HTMLElement) {
    const eventTarget = event.target as HTMLInputElement;
    mydrop.show(event, target);
    if (eventTarget.value != "") {
      this.deliveryAddress[1].status = false;
      this.deliveryAddress[2].status = false;
    } else {
      this.deliveryAddress[1].status = true;
      this.deliveryAddress[2].status = true;
    }
  }

  getAllBuyers() {
    const request = {
      clientAccountNumber:
        this.parent.get(["orderHeader", "customerIdValid"])?.value ||
        this.parent.get(["orderHeader", "customerId"])?.value ||
        "",
      buyer: "",
    } as GetAllBuyersRequestDto;
    this._buyerService
      .getAllBuyers(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          this.buyerLists = res.data;
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
  }

  searchBuyerName() {
    this.buyerVendor$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((searchitem: string) => {
          if (searchitem) {
            this.smallLoaderEvent.emit(true);
          }

          const request = {
            clientAccountNumber:
              this.parent.get(["orderHeader", "customerIdValid"])?.value ||
              this.parent.get(["orderHeader", "customerId"])?.value ||
              "",
            buyer: searchitem,
          } as GetAllBuyersRequestDto;

          this.buyerLists = [];
          if (searchitem) {
            return this._buyerService.getAllBuyers(request);
          }
          return EMPTY;
        })
      )
      .subscribe({
        next: (res: GenericResponseDto<GetAllBuyersResponseDto[]>) => {
          if (res.data != null) {
            this.buyerLists = res.data;
          }
          this.smallLoaderEvent.emit(false);
        },
        error: () => {
          this.smallLoaderEvent.emit(false);
        },
      });
  }

  logValidationErrors() {
    this.validationErrorEvent.emit();
  }

  getBuyer(event: Event, mydrop: Popover) {
    this.showSmallLoader = true;
    const keyboardEvent = event as KeyboardEvent;
    const val = event.target as HTMLInputElement;
    if (keyboardEvent.key.toLowerCase() === "tab") {
      const buyerName = val.value;
      const request = {
        clientAccountNumber:
          this.parent.get(["orderHeader", "customerIdValid"])?.value ||
          this.parent.get(["orderHeader", "customerId"])?.value ||
          "",
        buyer: buyerName,
      } as GetAllBuyersRequestDto;
      this._buyerService
        .getAllBuyers(request)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this.buyerLists = res.data;
            if (this.buyerLists.length > 0) {
              this.onBuyerSelection(this.buyerLists[0], mydrop);
            }
            this.showSmallLoader = false;
          },
          error: () => {
            this.showSmallLoader = false;
          },
        });
    }
  }

  onBuyerList(buyerdropdown: Popover, event?: Event) {
    buyerdropdown.show(event);
  }

  onBuyerSelection(
    event: GetAllBuyersResponseDto,
    buyerProspetToggle: Popover
  ) {
    this.buyerName = event.buyer;
    this.getCustomerAccount(event.account);
    this.onCustomerSelection(event.buyer);
    buyerProspetToggle.hide();
  }

  patchValueInCustomerAccount(accountNumber: string) {
    this.parent.patchValue({
      orderHeader: {
        customerId: accountNumber,
        customerIdValid: accountNumber,
      },
    });

    this.getBuyerEvent.emit();
  }

  getCustomerAccount(accountNumber: string) {
    this.showSmallLoader = true;
    this._buyerService
      .getCustomerByNameAndAccountNumberFilter(accountNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.customerLists = res.data;
          if (this.customerLists.length > 0) {
            this.onClientSelection(this.customerLists[0]);
          }
          this.showSmallLoader = false;
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
  }

  patchValueInCustomerAccountField(clientObj: ClientSearchList) {
    this.parent.patchValue({
      orderHeader: {
        accountNumberD365: clientObj.clientAccount,
      },
    });
  }

  patchValueInCustomerAddressField() {
    this._buyerService
      .getCustomerAddressByPartyNumber(
        this.customerBillToObject?.partyNumber,
        this.customerBillToObject?.clientAccount
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          const primaryAddress = res.data.filter(
            x =>
              x.isPrimaryD365?.toLowerCase() === "yes" ||
              x.isPrimaryD365?.toLowerCase() === "1"
          );

          if (primaryAddress.length > 0) {
            if (primaryAddress[0].addressD365) {
              this.patchValuesInCustomerFormAddressField(
                primaryAddress[0].addressD365
              );
            }
          } else {
            this.patchValuesInCustomerFormAddressField("");
          }
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
  }

  patchValuesInCustomerFormAddressField(address: string) {
    this.parent.patchValue({
      customerAddress: {
        address: address,
      },
    });
  }

  onClientSelection(event: ClientSearchList) {
    this.selectedAccountNumber = event.clientAccount;
    this.clearInvoiceEmails();
    this.patchValueInCustomerAccount(this.selectedAccountNumber);
    this.customerBillToObject = event;
    this.customerBillToObjectEvent.emit(this.customerBillToObject);
    if (event.clientAccount != "") {
      this.deliveryAddress[1].status = false;
      this.deliveryAddress[2].status = false;
    } else {
      this.deliveryAddress[1].status = true;
      this.deliveryAddress[2].status = true;
    }
    this.patchValueInCustomerNameAndTelephoneField(event);
    this.patchValueInCustomerAccountField(event);
    this.patchValueInCustomerAddressField();
    // this.searchCustomerName();
  }

  clearInvoiceEmails() {
    (this.parent.get("invoicedEmails") as FormArray).clear();
  }

  patchValueInCustomerNameAndTelephoneField(clientObj: ClientSearchList) {
    this.parent.patchValue({
      customerAddress: {
        name: clientObj.organizationNameD365,
        telephone: clientObj.locator === null ? "" : clientObj.locator,
      },
    });
  }

  onContactList(contactdropdown: Popover, event: MouseEvent) {
    contactdropdown.show(event);
  }

  applyBuyerLengthValidation(event: Event) {
    const eventTarget = event.target as HTMLInputElement;
    if (eventTarget.value) {
      this.parent.patchValue({
        orderHeader: {
          buyer: eventTarget.value.substring(0, 50),
        },
      });
    }
  }

  onCustomerSelection(name: string) {
    this.parent.patchValue({
      orderHeader: {
        buyer: name,
      },
    });
  }
}
